import { create } from "zustand";
import { logger } from "@/utils/logger";
import { IStore } from "@/types/interfaces";
import {
  ASSET_ERROR_MSG,
  SERVER_ERROR_MSG,
  NETWORK_ERROR_MSG,
  TIMEOUT_ERROR_MSG,
  UNAUTHORIZED_MSG,
  FORBIDDEN_MSG,
  NOT_FOUND_MSG,
  INTERNAL_SERVER_ERROR_MSG,
} from "@/constants/messages";

export interface ErrorEntry {
  id: string;
  type:
    | "asset"
    | "scene"
    | "store"
    | "validation"
    | "network"
    | "permission"
    | "timeout"
    | "resource"
    | "configuration"
    | "unknown";
  message: string;
  details?: string;
  timestamp: Date;
  resolved: boolean;
  severity: "low" | "medium" | "high" | "critical";
  context?: Record<string, any>;
  stackTrace?: string;
}

export interface ErrorState {
  errors: ErrorEntry[];
  isInitialized: boolean;
  errorCount: number;
  criticalErrorCount: number;
}

interface ErrorActions extends IStore {

  // Actions
  addError: (
    type: ErrorEntry["type"],
    message: string,
    details?: string,
    severity?: ErrorEntry["severity"],
    context?: Record<string, any>,
    stackTrace?: string
  ) => string;

  resolveError: (errorId: string) => void;
  removeError: (errorId: string) => void;
  clearResolvedErrors: () => void;
  clearAllErrors: () => void;

  // Convenience methods for common error types
  addAssetError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addSceneError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addStoreError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addValidationError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addNetworkError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addPermissionError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addTimeoutError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addResourceError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;
  addConfigurationError: (
    message: string,
    details?: string,
    context?: Record<string, any>
  ) => string;

  // Getters
  getUnresolvedErrors: () => ErrorEntry[];
  getErrorsByType: (type: ErrorEntry["type"]) => ErrorEntry[];
  getErrorsBySeverity: (severity: ErrorEntry["severity"]) => ErrorEntry[];
  getCriticalErrors: () => ErrorEntry[];
  hasUnresolvedErrors: () => boolean;
  hasCriticalErrors: () => boolean;
  getErrorById: (errorId: string) => ErrorEntry | undefined;
  getLatestError: () => ErrorEntry | undefined;
  getErrorSummary: () => {
    total: number;
    resolved: number;
    unresolved: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
  };
}

const initialState: ErrorState = {
  errors: [],
  isInitialized: false,
  errorCount: 0,
  criticalErrorCount: 0,
};

export const useErrorStore = create<ErrorState & ErrorActions>(
  (set: any, get: any) => ({
    ...initialState,

    initialize: async (): Promise<boolean> => {
      try {
        set((state: ErrorState) => ({
          ...state,
          isInitialized: true,
        }));

        return true;
      } catch (error) {
        logger.error("Failed to initialize ErrorStore", error, "ErrorStore");
        return false;
      }
    },

    reset: () => {
      set(initialState);
    },

    getStatus: () => {
      const state = get();
      return {
        isInitialized: state.isInitialized,
      };
    },

    addError: (
      type: ErrorEntry["type"],
      message: string,
      details?: string,
      severity: ErrorEntry["severity"] = "medium",
      context?: Record<string, any>,
      stackTrace?: string
    ) => {
      const error: ErrorEntry = {
        id: Date.now().toString(),
        type,
        message,
        details,
        timestamp: new Date(),
        resolved: false,
        severity,
        context,
        stackTrace,
      };

      set((state: ErrorState) => ({
        ...state,
        errors: [...state.errors, error],
        errorCount: state.errorCount + 1,
        criticalErrorCount:
          severity === "critical"
            ? state.criticalErrorCount + 1
            : state.criticalErrorCount,
      }));

      logger.error(
        `Error added to store: ${message}`,
        { error, context },
        "ErrorStore"
      );
      return error.id;
    },

    resolveError: (errorId: string) => {
      set((state: ErrorState) => {
        const error = state.errors.find((e: ErrorEntry) => e.id === errorId);
        if (error && !error.resolved) {
          const updatedErrors = state.errors.map((e: ErrorEntry) =>
            e.id === errorId ? { ...e, resolved: true } : e
          );
          return {
            ...state,
            errors: updatedErrors,
            criticalErrorCount:
              error.severity === "critical"
                ? Math.max(0, state.criticalErrorCount - 1)
                : state.criticalErrorCount,
          };
        }
        return state;
      });

      logger.info(`Error resolved`, { errorId }, "ErrorStore");
    },

    removeError: (errorId: string) => {
      set((state: ErrorState) => {
        const errorIndex = state.errors.findIndex(
          (e: ErrorEntry) => e.id === errorId
        );
        if (errorIndex !== -1) {
          const error = state.errors[errorIndex];
          const updatedErrors = state.errors.filter(
            (_: ErrorEntry, index: number) => index !== errorIndex
          );
          return {
            ...state,
            errors: updatedErrors,
            errorCount: state.errorCount - 1,
            criticalErrorCount:
              error.severity === "critical" && !error.resolved
                ? Math.max(0, state.criticalErrorCount - 1)
                : state.criticalErrorCount,
          };
        }
        return state;
      });

      logger.info(`Error removed`, { errorId }, "ErrorStore");
    },

    clearResolvedErrors: () => {
      set((state: ErrorState) => {
        const resolvedCount = state.errors.filter(
          (e: ErrorEntry) => e.resolved
        ).length;
        return {
          ...state,
          errors: state.errors.filter((e: ErrorEntry) => !e.resolved),
          errorCount: state.errorCount - resolvedCount,
        };
      });

      logger.info(`Cleared resolved errors`, null, "ErrorStore");
    },

    clearAllErrors: () => {
      set((state: ErrorState) => ({
        ...state,
        errors: [],
        errorCount: 0,
        criticalErrorCount: 0,
      }));

      logger.info("All errors cleared", null, "ErrorStore");
    },

    // Convenience methods for common error types
    addAssetError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "asset",
        message || ASSET_ERROR_MSG,
        details,
        "medium",
        context
      );
    },

    addSceneError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "scene",
        message || SERVER_ERROR_MSG,
        details,
        "high",
        context
      );
    },

    addStoreError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "store",
        message || SERVER_ERROR_MSG,
        details,
        "medium",
        context
      );
    },

    addValidationError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "validation",
        message || "Validation error occurred",
        details,
        "low",
        context
      );
    },

    addNetworkError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "network",
        message || NETWORK_ERROR_MSG,
        details,
        "high",
        context
      );
    },

    addPermissionError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "permission",
        message || FORBIDDEN_MSG,
        details,
        "high",
        context
      );
    },

    addTimeoutError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "timeout",
        message || TIMEOUT_ERROR_MSG,
        details,
        "medium",
        context
      );
    },

    addResourceError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "resource",
        message || NOT_FOUND_MSG,
        details,
        "high",
        context
      );
    },

    addConfigurationError: (
      message: string,
      details?: string,
      context?: Record<string, any>
    ) => {
      return get().addError(
        "configuration",
        message || INTERNAL_SERVER_ERROR_MSG,
        details,
        "critical",
        context
      );
    },

    // Getters
    getUnresolvedErrors: () => {
      const state = get();
      return state.errors.filter((e: ErrorEntry) => !e.resolved);
    },

    getErrorsByType: (type: ErrorEntry["type"]) => {
      const state = get();
      return state.errors.filter((e: ErrorEntry) => e.type === type);
    },

    getErrorsBySeverity: (severity: ErrorEntry["severity"]) => {
      const state = get();
      return state.errors.filter((e: ErrorEntry) => e.severity === severity);
    },

    getCriticalErrors: () => {
      const state = get();
      return state.errors.filter(
        (e: ErrorEntry) => e.severity === "critical" && !e.resolved
      );
    },

    hasUnresolvedErrors: () => {
      const state = get();
      return state.errors.some((e: ErrorEntry) => !e.resolved);
    },

    hasCriticalErrors: () => {
      const state = get();
      return state.criticalErrorCount > 0;
    },

    getErrorById: (errorId: string) => {
      const state = get();
      return state.errors.find((e: ErrorEntry) => e.id === errorId);
    },

    getLatestError: () => {
      const state = get();
      return state.errors.length > 0
        ? state.errors[state.errors.length - 1]
        : undefined;
    },

    getErrorSummary: () => {
      const state = get();
      const byType: Record<string, number> = {};
      const bySeverity: Record<string, number> = {};

      state.errors.forEach((error: ErrorEntry) => {
        byType[error.type] = (byType[error.type] || 0) + 1;
        bySeverity[error.severity] = (bySeverity[error.severity] || 0) + 1;
      });

      return {
        total: state.errors.length,
        resolved: state.errors.filter((e: ErrorEntry) => e.resolved).length,
        unresolved: state.errors.filter((e: ErrorEntry) => !e.resolved).length,
        byType,
        bySeverity,
      };
    },
  })
);
